import React, { useState, useEffect, useRef, useMemo } from 'react';
import AttachmentIcon from '../Icons/AttachmentIcon';
import MicrophoneIcon from '../Icons/MicrophoneIcon';
import AIVoiceIcon from '../Icons/AIVoiceIcon';
import WaveCloseIcon from '../Icons/WaveCloseIcon';
import CheckIcon from '../Icons/CheckIcon';
import TopArrow from '../Icons/TopArrow';
import Disclaimer from '../Disclaimer';
import useBubbleActions from '@/hooks/useBubbleActions';
import useFileUploadPermission from '@/hooks/useFileUploadPermission';
import { ACCEPTED_IMAGE_TYPES } from '@/constants';
import MarkResolvedIcon from '../Icons/MarkResolvedIcon';
import DTooltip from '@/components/Global/DTooltip';
import UploadWrapper from '../UploadWrapper';

const MIN_BAR = 4;    // px
const MAX_BAR = 16;   // px
const BAR_WIDTH_PX = 1;     // visual width per bar
const DESIRED_GAP_PX = 2;   // used to estimate how many bars can fit

const MessageInput = ({
  placeholder = 'Message',
  value = '',
  onChange,
  onMicrophoneClick,
  onAIVoiceClick,
  onSend,
  className = '',
  disabled = false,
  showDisclaimer = true,
  onDisclaimerClose,
  config = {},
  isInApp = false,
  isPreviewMode = false,
  isInHumanHandoverApp = false,
  pollResponse = false,
  onImageUpload,
  onFileUpload,
  onMarkResolvedClick,
  onEnableFileClick,
  uploadedFiles = [],
  uploadedImages = [],
  onRemoveFile,
  onRemoveImage,
  isLoading = false,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMicActive, setIsMicActive] = useState(false);
  const [transcript, setTranscript] = useState('');

  // Check if file upload is allowed based on context and settings
  const isFileUploadAllowed = useFileUploadPermission(
    isInHumanHandoverApp,
    pollResponse,
    config?.token,
    config?.conversationId
  );
  const fileInputRef = useRef(null);


  // Initialize disclaimer visibility from session storage
  const [isDisclaimerVisible, setIsDisclaimerVisible] = useState(() => {
    const sessionDismissed = sessionStorage.getItem('disclaimerDismissed');
    const shouldShowBasedOnConfig = config?.disclaimer_enabled && (!isInApp || isPreviewMode);
    // In preview mode, always show disclaimer when enabled (ignore session dismissal)
    if (isPreviewMode && config?.disclaimer_enabled && showDisclaimer) {
      return true;
    }
    return sessionDismissed !== 'true' && showDisclaimer && shouldShowBasedOnConfig && !isInHumanHandoverApp;
  });

  const conversationInterfaceType = isInHumanHandoverApp
    ? 'human-handover'
    : isInApp
    ? 'in-app'
    : 'bubble';
  const { openPrivacyPolicy } = useBubbleActions(config, conversationInterfaceType);

  const originalTextRef = useRef('');
  const originalHeightRef = useRef('24px'); // Store original height before recording
  const recognitionRef = useRef(null);
  const textareaRef = useRef(null);

  // Handle disclaimer close
  const handleDisclaimerClose = () => {
    // In preview mode, don't allow dismissal - disclaimer should always be visible when enabled
    if (isPreviewMode) {
      return;
    }

    setIsDisclaimerVisible(false);
    // Store dismissal state in session storage
    sessionStorage.setItem('disclaimerDismissed', 'true');
    if (onDisclaimerClose) {
      onDisclaimerClose();
    }
  };

  // Sync internal state with prop and session storage
  useEffect(() => {
    const sessionDismissed = sessionStorage.getItem('disclaimerDismissed');
    // Apply the same logic as the original disclaimer: config?.disclaimer_enabled && (!isInApp || isPreviewMode)
    const shouldShowBasedOnConfig = config?.disclaimer_enabled && (!isInApp || isPreviewMode);

    // In preview mode, always show disclaimer when enabled (ignore session dismissal)
    if (isPreviewMode && config?.disclaimer_enabled && showDisclaimer) {
      setIsDisclaimerVisible(true);
      return;
    }

    setIsDisclaimerVisible(sessionDismissed !== 'true' && showDisclaimer && shouldShowBasedOnConfig);
  }, [showDisclaimer, config?.disclaimer_enabled, isInApp, isPreviewMode]);



  // Audio
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const rafRef = useRef(null);

  // Bars
  const containerRef = useRef(null);
  const [barCount, setBarCount] = useState(79); // will be recalculated by ResizeObserver
  const [barHeights, setBarHeights] = useState(() =>
    Array.from({ length: 79 }, () => MIN_BAR)
  );

  // ---- ResizeObserver: compute how many bars fit in the space ----
  useEffect(() => {
    if (!containerRef.current) return;

    const el = containerRef.current;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const perBar = BAR_WIDTH_PX + DESIRED_GAP_PX; // rough packing heuristic
        const count = Math.max(10, Math.floor(width / perBar)); // keep a sensible minimum
        setBarCount(count);
      }
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Keep barHeights array in sync with barCount
  useEffect(() => {
    setBarHeights(prev => {
      if (prev.length === barCount) return prev;
      // Resize gracefully: keep old values where possible
      const next = Array.from({ length: barCount }, (_, i) => prev[i] ?? MIN_BAR);
      return next;
    });
  }, [barCount]);

  // ---- Speech recognition (same behavior as before) ----
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('Speech recognition not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-US';

    recognitionRef.current.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';
      for (let i = 0; i < event.results.length; i++) {
        const transcriptPiece = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcriptPiece + ' ';
        } else {
          interimTranscript += transcriptPiece;
        }
      }
      setTranscript((finalTranscript.trim() + ' ' + interimTranscript.trim()).trim());
    };

    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsMicActive(false);
    };

    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
    };
  }, []);

  // ---- Mic start/stop + analyser loop ----
  const startMic = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      }
    });

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioContextClass();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();

    analyser.fftSize = 256;               // 256 samples for time-domain
    analyser.smoothingTimeConstant = 0.85;

    source.connect(analyser);

    audioCtxRef.current = audioCtx;
    analyserRef.current = analyser;
    mediaStreamRef.current = stream;

    loopVisuals();
  };

  const stopMic = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch {}
      audioCtxRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    analyserRef.current = null;
    // Reset bars to baseline grey
    setBarHeights(prev => prev.map(() => MIN_BAR));
  };

  const loopVisuals = () => {
    if (!analyserRef.current) return;
    const analyser = analyserRef.current;
    const timeData = new Uint8Array(analyser.fftSize);

    const update = () => {
      analyser.getByteTimeDomainData(timeData);


      const len = timeData.length;
      const nextHeights = new Array(barCount);

      for (let i = 0; i < barCount; i++) {
        // Sample index across the buffer
        const idx = Math.min(len - 1, Math.max(0, Math.round((i / Math.max(1, barCount - 1)) * (len - 1))));
        const v = Math.abs((timeData[idx] - 128) / 128); // 0..1

        // Apply scaling with noise threshold
        let scaled = 0;
        if (v > 0.03) { // Only process if above noise threshold
          const adjustedVolume = Math.max(0, v - 0.03) / (0.5 - 0.03); // Normalize to reach max at 0.5 volume
          const bias = Math.pow(i / Math.max(1, barCount - 1), 1.7);
          scaled = Math.min(1, adjustedVolume * 1.2 + bias * 0.2); // Increased sensitivity
        }

        const h = MIN_BAR + Math.round(scaled * (MAX_BAR - MIN_BAR));
        nextHeights[i] = Math.max(MIN_BAR, Math.min(MAX_BAR, h));
      }

      setBarHeights(nextHeights);
      rafRef.current = requestAnimationFrame(update);
    };

    rafRef.current = requestAnimationFrame(update);
  };

  // ---- Handlers ----
  const handleMicrophoneClick = async () => {
    const newMicState = !isMicActive;

    if (newMicState) {
      // Store current height before starting recording
      if (textareaRef.current) {
        const currentHeight = textareaRef.current.style.height;
        originalHeightRef.current = currentHeight || '24px';
      }

      // Set mic active FIRST to enable height preservation
      setIsMicActive(true);

      originalTextRef.current = value || '';
      setTranscript('');

      // Clear textarea to show Listening...
      if (onChange) onChange({ target: { value: '' } });

      try {
        await startMic();
        if (recognitionRef.current) recognitionRef.current.start();
      } catch (e) {
        console.error('Error starting mic:', e);
        stopMic();
        setIsMicActive(false);
        return;
      }
    } else {
      if (recognitionRef.current) recognitionRef.current.stop();
      stopMic();
      setIsMicActive(false);
    }
    if (onMicrophoneClick) onMicrophoneClick();
  };

  const handleWaveCloseClick = () => {
    setIsMicActive(false);
    setTranscript('');
    if (recognitionRef.current) recognitionRef.current.stop();
    stopMic();

    // Restore original text
    if (onChange) onChange({ target: { value: originalTextRef.current } });

    // Force height recalculation after restoring original text and refocus
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        const scrollHeight = textareaRef.current.scrollHeight;
        const maxHeight = 120;
        const minHeight = 24;
        const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));

        textareaRef.current.style.height = newHeight + 'px';

        // Enable scrolling if content exceeds max height
        if (scrollHeight > maxHeight) {
          textareaRef.current.style.overflowY = 'auto';
        } else {
          textareaRef.current.style.overflowY = 'hidden';
        }

        // Refocus the textarea after canceling the recording
        if (!disabled && !isPreviewMode) {
          textareaRef.current.focus({ preventScroll: true });
        }
      }
    }, 0);
  };

  const handleCheckClick = () => {
    const combinedText =
      originalTextRef.current && transcript
        ? `${originalTextRef.current} ${transcript}`
        : (originalTextRef.current || transcript);

    setIsMicActive(false);
    if (recognitionRef.current) recognitionRef.current.stop();
    stopMic();

    if (onChange) onChange({ target: { value: combinedText } });
    setTranscript('');

    // Force height recalculation after setting the combined text and refocus
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        const scrollHeight = textareaRef.current.scrollHeight;
        const maxHeight = 120;
        const minHeight = 24;
        const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));

        textareaRef.current.style.height = newHeight + 'px';

        // Enable scrolling if content exceeds max height
        if (scrollHeight > maxHeight) {
          textareaRef.current.style.overflowY = 'auto';
        } else {
          textareaRef.current.style.overflowY = 'hidden';
        }

        // Refocus the textarea after accepting the transcript
        if (!disabled && !isPreviewMode) {
          textareaRef.current.focus({ preventScroll: true });
        }
      }
    }, 0);
  };

  // Handle AI Voice button click - simple implementation following existing pattern
  const handleAIVoiceClick = () => {
    if (disabled || !onAIVoiceClick) return;

    // Simply call the voice handler - all checks happen in VoicePreview component
    onAIVoiceClick();
  };

  // Handle sending message
  const handleSendMessage = () => {
    if (onSend) {
      onSend();
      // Refocus the textarea after sending the message
      // Use requestAnimationFrame to ensure DOM updates complete
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (textareaRef.current && !disabled && !isPreviewMode) {
            textareaRef.current.focus({ preventScroll: true });
          }
        }, 10);
      });
    }
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Auto-resize textarea with scroll support
  useEffect(() => {
    if (textareaRef.current) {
      if (isMicActive) {
        // During recording, maintain the stored original height
        textareaRef.current.style.height = originalHeightRef.current;
      } else {
        // Normal text mode - calculate height based on content
        textareaRef.current.style.height = 'auto';
        const scrollHeight = textareaRef.current.scrollHeight;
        const maxHeight = 120;
        const minHeight = 24;
        const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));

        textareaRef.current.style.height = newHeight + 'px';

        // If content exceeds max height, enable scrolling
        if (scrollHeight > maxHeight) {
          textareaRef.current.style.overflowY = 'auto';
        } else {
          textareaRef.current.style.overflowY = 'hidden';
        }
      }
    }
  }, [value, isMicActive]);

 
  const prevDisabledRef = useRef(disabled);
  useEffect(() => {
    if (prevDisabledRef.current && !disabled && !isPreviewMode && !isMicActive) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus({ preventScroll: true });
          }
        }, 50);
      });
    }
    prevDisabledRef.current = disabled;
  }, [disabled, isPreviewMode, isMicActive]);


  const prevValueRef = useRef(value);
  useEffect(() => {
    if (prevValueRef.current && prevValueRef.current.trim().length > 0 &&
        (!value || value.trim().length === 0) && !disabled && !isPreviewMode && !isMicActive) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus({ preventScroll: true });
          }
        }, 100);
      });
    }
    prevValueRef.current = value;
  }, [value, disabled, isPreviewMode, isMicActive]);

  const getBorderClasses = () => {
    const hasFiles = uploadedFiles.length > 0 || uploadedImages.length > 0;

    if (hasFiles) {
      return 'border-t border-l border-r border-v3-black-border';
    } else {
      // Normal full border with focus/hover effects when no files
      if (isFocused) return 'border border-v3-black-primary';
      if (isHovered) return 'border border-v3-black-placeholder';
      return 'border border-v3-black-border';
    }
  };

  // ---- Bars (per-bar color based on its current height) ----
  const Bars = () => {
    return (
      <div ref={containerRef} className="flex items-center justify-between w-full px-v3-md" style={{ height: `${MAX_BAR}px` }}>
        {barHeights.map((h, i) => {
          const isActive = h > (MIN_BAR + 2); // a bit above baseline = "active"
          const barClass = isActive ? 'bg-v3-black-max' : 'bg-v3-black-inactive';
          return (
            <div
              key={i}
              className={`${barClass} rounded-full`}
              style={{
                width: `${BAR_WIDTH_PX}px`,
                height: `${h}px`,
              }}
            />
          );
        })}
      </div>
    );
  };





  return (
    <div className='bg-v3-black-fixed-card rounded-v3-xl'>
      <div
      className={`
        flex
        w-full
        p-v3-sm
        flex-col
        justify-center
        items-start
        gap-v3-xs
        ${(uploadedFiles.length > 0 || uploadedImages.length > 0) ? 'rounded-t-v3-xl' : `rounded-v3-xl ${!isFocused ? 'shadow-v3-double' : ''}`}
        bg-[var(--dt-color-surface-100)]
        transition-all
        duration-200
        ${getBorderClasses()}
        ${disabled || isPreviewMode ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
      style={{
        ...(uploadedFiles.length > 0 || uploadedImages.length > 0 && {
          borderBottom: 'none'
        })
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={isMicActive ? 'Listening...' : placeholder}
        disabled={isMicActive || isPreviewMode}
        rows={1}
        className="
          w-full
          resize-none
          bg-transparent
          border-none
          outline-none
          text-[var(--dt-color-element-100)]
          placeholder-v3-black-placeholder
          font-regular
          text-base
          leading-[160%]
          overflow-y-auto
          scrollbar-hide
        "
        style={{
          fontFamily: 'var(--dt-font-family, "Inter Variable", Inter, sans-serif)',
          minHeight: '24px',
          maxHeight: '100px',
          height: 'auto',
          fontSize: '15px'
        }}
        onInput={(e) => {
          e.target.style.height = 'auto';
          const scrollHeight = e.target.scrollHeight;
          const maxHeight = 120;
          const minHeight = 24;
          const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));

          e.target.style.height = newHeight + 'px';

          // Enable scrolling if content exceeds max height
          if (scrollHeight > maxHeight) {
            e.target.style.overflowY = 'auto';
          } else {
            e.target.style.overflowY = 'hidden';
          }
        }}
      />

      {/* Bottom Icons Bar */}
      <div className="flex justify-between items-center self-stretch w-full">
        {isMicActive ? (
          <>
            {/* Left - Close */}
            <button
              onClick={handleWaveCloseClick}
              disabled={disabled}
              className="
                p-v3-xxs
                rounded-v3-xs
                transition-colors
                duration-200
                disabled:opacity-50
                disabled:cursor-not-allowed
              
              "
            >
              <WaveCloseIcon />
            </button>

            {/* Center - Bars */}
            <div className="flex-1 flex w-full">
              <Bars />
            </div>

            {/* Right - Check */}
            <button
              onClick={handleCheckClick}
              disabled={disabled}
              className="
                flex
                p-v3-xxs
                items-center
                gap-v3-xs
                rounded-[30px]
                bg-v3-purple-primary
                hover:bg-v3-purple-dark
                transition-colors
                duration-200
                disabled:opacity-50
                disabled:cursor-not-allowed
              "
            >
              <CheckIcon />
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-v3-xs">
              {isFileUploadAllowed && (
                <input
                  type="file"
                  accept=".pdf,.json,.xml,.docx,.doc,.txt,.csv,.xlsx,.ppt,.pptx,.jpeg,.jpg,.png,.svg,.md,.epub,.mbox,.rtf,.html"
                  multiple
                  className="hidden"
                  ref={fileInputRef}
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                

                    const imageFiles = files.filter(file => ACCEPTED_IMAGE_TYPES.includes(file.type));
                    const otherFiles = files.filter(file => !ACCEPTED_IMAGE_TYPES.includes(file.type));


                    // Handle images
                    if (imageFiles.length > 0 && onImageUpload) {
                      onImageUpload(imageFiles);
                    }

                    // Handle other files
                    if (otherFiles.length > 0 && onFileUpload) {
                      onFileUpload(otherFiles);
                    }

                    // Clear the input value to allow selecting the same file again
      
                    e.target.value = '';
                  }}
                  tabIndex={-1}
                />
              )}

              {/* Show actual file upload button when file upload is allowed */}
              {isFileUploadAllowed && (
                <DTooltip content="Upload file" position='right'>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || isPreviewMode}
                    className="
                      p-v3-xxs
                      rounded-v3-xs
                      hover:bg-v3-black-card
                      transition-colors
                      duration-200
                      disabled:opacity-50
                      disabled:cursor-not-allowed
                    "
                  >
                    <AttachmentIcon  className={`${isInHumanHandoverApp && isFileUploadAllowed ? 'text-v3-green-primary' : ''}`}/>
                  </button>
                </DTooltip>
              )}

              {/* Show enable file upload button when in human handover app */}
              {isInHumanHandoverApp && !isFileUploadAllowed && onEnableFileClick && (
                <DTooltip content="Enable file upload" position='right'>
                  <button
                    onClick={onEnableFileClick}
                    disabled={disabled}
                    className="
                      p-v3-xxs
                      rounded-v3-xs
                      hover:bg-v3-black-card
                      transition-colors
                      duration-200
                      disabled:opacity-50
                      disabled:cursor-not-allowed
                    "
                  >
                    <AttachmentIcon   />
              
                  </button>
                 
              
                </DTooltip>
              )}

              {config?.show_microphone && (
                <button
                  onClick={handleMicrophoneClick}
                  disabled={disabled || isPreviewMode}
                  className="
                    bubble-microphone-button
                    p-v3-xxs
                    rounded-v3-xs
                    hover:bg-v3-black-card
                    transition-colors
                    duration-200
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                >
                  <MicrophoneIcon />
                </button>
              )}

              {isInHumanHandoverApp && (
                <>
                  <DTooltip content="Mark as resolved" position='right'>
                    <button
                      onClick={onMarkResolvedClick}
                      disabled={disabled}
                      className="
                        p-v3-xxs
                        rounded-v3-xs
                        hover:bg-v3-black-card
                        transition-colors
                        duration-200
                        disabled:opacity-50
                        disabled:cursor-not-allowed
                      "
                    >
                      <MarkResolvedIcon />
                    </button>
                  </DTooltip>

                 
                </>
              )}
            </div>

            {/* Right side buttons - conditional rendering */}
            {config?.realtime_voice_access && !isInHumanHandoverApp ? (

              (value.trim() || uploadedFiles.length > 0 || uploadedImages.length > 0) ? (
                // Send button when there's text
                <button
                  onClick={handleSendMessage}
                  disabled={disabled || isPreviewMode || isLoading}
                  className="
                    bubble-send-button
                    flex
                    p-v3-xxs
                    items-center
                    gap-v3-xs
                    rounded-[30px]
                    bg-[var(--dt-color-brand-100)]
                    hover:bg-[var(--dt-color-brand-60)]
                    transition-colors
                    duration-200
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                >
                  <TopArrow />
                </button>
              ) : (
                // AI Voice button when there's no text and voice is enabled
                <button
                  onClick={handleAIVoiceClick}
                  disabled={disabled || isPreviewMode || isLoading}
                  className="
                    bubble-ai-voice-button
                    flex
                    p-v3-xxs
                    items-center
                    gap-v3-xs
                    rounded-[30px]
                    bg-[var(--dt-color-brand-100)]
                    hover:bg-[var(--dt-color-brand-60)]
                    transition-colors
                    duration-200
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                  title="Start voice conversation"
                >
                  <AIVoiceIcon />
                </button>
              )
            ) : (
              // When voice is disabled: Always show Send button
              <button
                onClick={handleSendMessage}
                disabled={disabled || isPreviewMode || isLoading}
                className="
                  bubble-send-button
                  flex
                  p-v3-xxs
                  items-center
                  gap-v3-xs
                  rounded-[30px]
                  bg-[var(--dt-color-brand-100)]
                  hover:bg-[var(--dt-color-brand-60)]
                  transition-colors
                  duration-200
                  disabled:opacity-50
                  disabled:cursor-not-allowed
                "
              >
                <TopArrow />
              </button>
            )}
          </>
        )}
      </div>
    </div>
      {/* Upload Wrapper for file previews */}
      {(uploadedFiles.length > 0 || uploadedImages.length > 0) && (
        <div className="animate-fadeIn">
          <UploadWrapper
            files={[...uploadedFiles, ...uploadedImages]}
            onRemoveFile={(index) => {
              // Determine if it's a file or image based on index
              if (index < uploadedFiles.length) {
                onRemoveFile && onRemoveFile(index);
              } else {
                const imageIndex = index - uploadedFiles.length;
                onRemoveImage && onRemoveImage(imageIndex);
              }
            }}
          />
        </div>
      )}
    {isDisclaimerVisible && !isInHumanHandoverApp && <Disclaimer onClick={handleDisclaimerClose} onPrivacyPolicyClick={openPrivacyPolicy} isPreviewMode={isPreviewMode} disclaimerText={config?.disclaimer_text} />}
    </div>
    
  );
};

export default MessageInput;
